/**
 * amountIntegrity — does the order still total what the customer actually paid? PURE.
 *
 * When a prepaid order is verified, Cashfree's exact paid amount is stored on the order
 * (`payment.amount_verified_paise`). If someone later edits the products, the order total
 * is recalculated — and can drift away from the money that was really received. That is
 * how an order the customer paid ₹673 for ended up reading ₹599: the ERP, the invoice and
 * the picking list would all understate what was sold, and the books would never balance.
 *
 * All comparisons are in integer paise. Never floating rupees.
 */

import { rupeesToPaise } from '../services/cashfreeReconcile';

const paiseOf = (v) => rupeesToPaise(v);

/** The amount actually received, in paise, or null if the payment was never verified. */
export const verifiedPaidPaise = (o) => {
  const p = o?.payment || {};
  if (String(p.verificationStatus || '').toUpperCase() !== 'PAID') return null;
  if (Number.isFinite(p.amount_verified_paise) && p.amount_verified_paise > 0) return p.amount_verified_paise;
  // A manually-marked payment may carry only the amount it was marked against.
  const a = paiseOf(p.paymentAudit?.amount);
  return a && a > 0 ? a : null;
};

/** The order's current expected total, in paise. */
export const currentTotalPaise = (o) => paiseOf(o?.payment?.total ?? o?.total);

/**
 * @returns {{ok: boolean, kind?: 'amount', paidPaise?: number, totalPaise?: number,
 *            deltaPaise?: number, short?: boolean, label?: string, hint?: string,
 *            blockMessage?: string}}
 *
 * `ok: true` also when there is nothing to compare (unverified payment, COD, missing
 * total) — this check only ever speaks when it KNOWS both numbers.
 */
export const amountIntegrity = (o) => {
  const paid = verifiedPaidPaise(o);
  if (paid === null) return { ok: true };
  const total = currentTotalPaise(o);
  if (total === null || total <= 0) return { ok: true };
  if (total === paid) return { ok: true };

  const delta = total - paid;           // negative = order now totals LESS than was paid
  const short = delta < 0;
  const rs = (p) => `₹${(Math.abs(p) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return {
    ok: false,
    kind: 'amount',
    paidPaise: paid,
    totalPaise: total,
    deltaPaise: delta,
    short,
    label: 'Amount mismatch',
    hint: `Paid ${rs(paid)} · order now ${rs(total)} (${short ? 'short by' : 'over by'} ${rs(delta)})`,
    blockMessage:
      `AMOUNT MISMATCH — this order cannot be shipped.\n\n` +
      `The customer paid ${rs(paid)}, but the order now totals ${rs(total)} ` +
      `(${short ? 'short by' : 'over by'} ${rs(delta)}).\n\n` +
      `This usually happens after the products were edited: the total was recalculated, ` +
      `but tax or the shipping charge was left out, or a different pack was selected.\n\n` +
      `Correct the order so its total matches the ${rs(paid)} actually received — ` +
      `or, if the customer really is owed a refund or a top-up, record that first.`,
  };
};

/** Convenience for lists and badges. */
export const hasAmountMismatch = (o) => amountIntegrity(o).ok === false;
